//@ts-check
import React from 'react';

import { useFormikContext } from 'formik';
import { Grid, makeStyles, Box } from '@material-ui/core';
import DeleteOutlinedIcon from '@material-ui/icons/DeleteOutlined';
import clsx from 'clsx';
import DropZone from '../components/DropZone';
import UploadTile from '../components/UploadTile';

const config = {
    fileExtensions: ['.jpg', '.jpeg', '.gif', '.png'],
    dimensions: {
        banner: { width: 640, height: 960 },
        product: { width: 640, height: 720 },
    },
};

const utils = {
    fileExtensionsRegex: RegExp(`${config.fileExtensions.join('|')}$`),
    isSupportedFileExtension: (ext) => utils.fileExtensionsRegex.test(ext),
    getFileDataURL: async (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = ({ target }) => resolve(target.result);
            reader.onerror = () => reject();
            reader.readAsDataURL(file);
        });
    },
    isWithinDimensions: (url, { width, height }) => {
        return new Promise((resolve) => {
            const onLoad = ({ target }) => {
                resolve(target.width <= width && target.height <= height);
            };

            const image = new Image();
            image.onload = onLoad;
            image.src = url;
        });
    },
    isWithinMegabyteLimit: (file, limit) => file.size / 1024 / 1024 <= limit,
};

const ImagesView = () => {
    const form = useFormikContext();
    const classes = useStyles();

    const { mainImage: bannerImage, images } = form.values.media;

    const deleteProductImage = (image) => {
        const filteredImages = images.filter((img) => img.url !== image.url);
        form.setFieldValue('media.images', filteredImages);
    };

    const setMainImage = (image) => form.setFieldValue('media.mainImage', image, true);

    const validateImage = async (file, type) => {
        const isSupportedExtension = utils.isSupportedFileExtension(file.name);

        if (!isSupportedExtension) {
            return { isValid: false };
        }

        // Get a base64 data URL that we can use to preview the file
        const url = await utils.getFileDataURL(file);

        const isCorrectSize = utils.isWithinMegabyteLimit(file, 2);
        const isCorrectDimensions = await utils.isWithinDimensions(url, config.dimensions[type]);

        const isValid = isCorrectSize && isCorrectDimensions;

        return {
            isValid,
            name: file.name,
            url,
        };
    };

    const onBannerImageSelected = async (files) => {
        const { isValid, ...image } = await validateImage(files[0], 'banner');
        if (isValid) {
            setMainImage(image);
        }
    };

    const processProductImages = async (files) => {
        const imagesToProcess = [...files]; // Convert FileList to array so we can `map` over it

        // TODO: Don't allow duplicate files ( check file name and/or url in existing images)

        const validatedImages = await Promise.all(
            imagesToProcess.map((image) => {
                return validateImage(image, 'product');
            }),
        );

        const imagesToAdd = validatedImages.filter((image) => image.isValid);

        form.setFieldValue('media.images', [...form.values.media.images, ...imagesToAdd], true);
    };

    return (
        <>
            <Grid container spacing={1} className={classes.content}>
                <Grid item xs={3}>
                    <Box className={classes.requirements}>
                        <p className={classes.text}>
                            <strong>Banner Image</strong> (Required)
                        </p>
                        <p className={classes.text}>
                            Recommended size:{' '}
                            <strong>
                                {config.dimensions.banner.width}x{config.dimensions.banner.height}
                            </strong>
                        </p>
                        <p className={classes.text}>
                            Maximum upload file size: <strong>2MB</strong>
                        </p>
                        <p className={classes.text}>
                            Accepted formats:{' '}
                            <strong>
                                {config.fileExtensions.map((ext) => ext.replace('.', '').toUpperCase()).join(', ')}
                            </strong>
                        </p>
                    </Box>
                    <DropZone component={Grid} container spacing={3} onDrop={onBannerImageSelected}>
                        {bannerImage && bannerImage.url ? (
                            <Grid item className={classes.imageGalleryItem}>
                                <img src={bannerImage.url} alt="" width={320} height={480} />
                                <p
                                    className={clsx(classes.text, classes.deleteButton)}
                                    onClick={() => setMainImage(undefined)}
                                >
                                    <DeleteOutlinedIcon />
                                    <strong>
                                        <u>Delete Image</u>
                                    </strong>
                                </p>
                            </Grid>
                        ) : (
                            <UploadTile
                                onSelection={onBannerImageSelected}
                                accept={config.fileExtensions.join(',')}
                                height={480}
                                hasError={form.errors && form.errors.media && form.errors.media.mainImage}
                            />
                        )}
                    </DropZone>
                </Grid>
                <Grid item xs={9}>
                    <Box className={classes.requirements}>
                        <p className={classes.text}>
                            <strong>Product Image(s)</strong> (Required)
                        </p>
                        <p className={classes.text}>
                            Recommended size:{' '}
                            <strong>
                                {config.dimensions.product.width}x{config.dimensions.product.height}
                            </strong>
                        </p>
                        <p className={classes.text}>
                            Maximum upload file size: <strong>2MB</strong>
                        </p>
                        <p className={classes.text}>
                            Accepted formats:{' '}
                            <strong>
                                {config.fileExtensions.map((ext) => ext.replace('.', '').toUpperCase()).join(', ')}
                            </strong>
                        </p>
                    </Box>
                    <DropZone component={Grid} container spacing={3} onDrop={processProductImages}>
                        {images.map((image) => {
                            return (
                                <Grid item className={classes.imageGalleryItem} key={image.url}>
                                    <img src={image.url} alt="" width={320} height={360} />
                                    <p
                                        className={clsx(classes.text, classes.deleteButton)}
                                        onClick={() => deleteProductImage(image)}
                                    >
                                        <DeleteOutlinedIcon />
                                        <strong>
                                            <u>Delete Image</u>
                                        </strong>
                                    </p>
                                </Grid>
                            );
                        })}
                        <Grid item className={clsx(classes.imageGalleryItem)}>
                            <UploadTile
                                onSelection={processProductImages}
                                accept={config.fileExtensions.join(',')}
                                multiple
                                hasError={form.errors && form.errors.media && form.errors.media.images}
                            />
                        </Grid>
                    </DropZone>
                </Grid>
            </Grid>
            <p className={classes.text} style={{ marginBottom: 18 }}>
                <strong>Please note:</strong> Images must be the correct dimensions and web optimised before uploading.
            </p>
        </>
    );
};

const useStyles = makeStyles((theme) => ({
    requirements: {
        marginBottom: theme.spacing(3),
    },
    text: {
        fontSize: 14,
        fontWeight: 400,
        fontFamily: 'Gotham SSm A',
        lineHeight: '14px',
        color: theme.palette.text.primary,
        margin: '5px 0',
        '&:first-child': {
            marginTop: '0',
            margin: '7.5px 0',
        },
    },
    errorText: {
        marginTop: 10,
        fontSize: 12,
        lineHeight: '16px',
        color: '#f00',
    },
    content: {
        marginBottom: 50,
    },
    imageGalleryItem: {
        maxWidth: 320,
        boxSizing: 'content-box',
    },
    deleteButton: {
        marginTop: theme.spacing(1),
        fontWeight: 500,
        cursor: 'pointer',
    },
}));

export default ImagesView;
